import { useState } from "react"
import { Link, useLocation } from "@tanstack/react-router"
import { Button } from "@uavity-core/ui/components/button"
import { cn } from "@uavity-core/ui/lib/utils"
import { Info, MapIcon, Radio, X } from "lucide-react"
import { useRealtimeContext } from "@/api/v1/realtime"
import { isTrackingRoute } from "@/constants/nav-routes"

const SEGMENT_LABELS: Record<string, string> = {
  map: "Map",
  sensors: "Sensors",
  geofences: "Geofences",
  "flight-restriction-zones": "Flight zones",
  "flight-plans": "Flight plans",
  "flight-plan": "Flight plan",
  analytics: "Analytics",
  account: "Account",
  preferences: "Preferences",
  subscriptions: "Subscriptions",
  support: "Support",
  legal: "Legal",
  "privacy-policy": "Privacy policy",
  "terms-of-use": "Terms of use",
  create: "Create",
  edit: "Edit",
  violations: "Violations",
  heatmap: "Activity heatmap",
  "detection-replay": "Detection replay",
}

function looksLikeId(segment: string): boolean {
  return (
    /^[0-9a-f-]{16,}$/i.test(segment) ||
    (segment.length > 24 && segment.includes("-"))
  )
}

function titleCaseSegment(segment: string): string {
  return segment
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function pathToCrumbs(pathname: string): string[] {
  if (pathname === "/" || pathname === "") return ["Map"]
  const parts = pathname.split("/").filter(Boolean)
  const out: string[] = []
  for (const p of parts) {
    if (SEGMENT_LABELS[p]) {
      out.push(SEGMENT_LABELS[p])
      continue
    }
    if (looksLikeId(p)) {
      out.push("Details")
      break
    }
    out.push(titleCaseSegment(p))
  }
  return out
}

export function AppChrome({ className }: { className?: string }) {
  const { pathname } = useLocation()
  const { connectionStats, isConnected } = useRealtimeContext()
  const [showDetails, setShowDetails] = useState(false)
  const onMap = pathname === "/" || pathname === "/map"
  const onTrackingRoute = isTrackingRoute(pathname)
  const title = onMap ? "Map" : pathToCrumbs(pathname).slice(-1)[0] ?? "Uavity"
  const live = isConnected && connectionStats.state === "connected"
  const status = live
    ? "Live"
    : connectionStats.state === "connecting"
      ? "Connecting"
      : connectionStats.state === "reconnecting" || connectionStats.state === "error"
        ? "Live updates unavailable — retrying"
        : "Live updates unavailable"

  return (
    <div className={cn("relative flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1", className)}>
      <h1 className="min-w-0 break-words text-lg font-semibold tracking-tight">{title}</h1>
      {onTrackingRoute ? (
        <div className="flex min-w-0 items-center gap-1">
          <span role="status" className={cn("flex min-w-0 items-center gap-2 text-xs sm:text-sm", live ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")}>
            <Radio className="h-4 w-4 shrink-0" aria-hidden />
            <span>{status}</span>
          </span>
          <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="Connection details" aria-expanded={showDetails} aria-controls="public-connection-details" onClick={() => setShowDetails(!showDetails)}>
            <Info className="h-4 w-4" aria-hidden />
          </Button>
          {showDetails && (
            <section id="public-connection-details" aria-label="Connection details" className="absolute right-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border bg-card p-4 text-sm shadow-xl">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold">Connection details</h2>
                <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Close connection details" onClick={() => setShowDetails(false)}><X className="h-4 w-4" aria-hidden /></Button>
              </div>
              <p className="mb-3 text-muted-foreground">A connected feed does not guarantee coverage in every area.</p>
              <dl className="space-y-2">
                <div className="flex justify-between gap-3"><dt>Connection</dt><dd>{connectionStats.state}</dd></div>
                <div className="flex justify-between gap-3"><dt>Observed sensors</dt><dd>{connectionStats.sensorCount}</dd></div>
                <div className="flex justify-between gap-3"><dt>Retry attempts</dt><dd>{connectionStats.reconnectAttempts}</dd></div>
              </dl>
              {connectionStats.lastError && <p className="mt-3 break-words rounded-lg bg-muted p-3 text-xs">{connectionStats.lastError}</p>}
            </section>
          )}
        </div>
      ) : (
        <Button asChild variant="outline" className="hidden min-h-11 sm:inline-flex"><Link to="/map"><MapIcon className="h-4 w-4" aria-hidden />Open map</Link></Button>
      )}
    </div>
  )
}
